import { useState, useRef, useCallback } from 'react';

export const useNoiseLevel = ({
  calibrationOffset = 138,
  deltaDuration = 3000,
  smoothingFactor = 0,
}) => {
  // calibrationOffset - калибровочное смещение

  const [isActiveMeasurement, setIsActiveMeasurement] = useState(false);
  const [noiseLevelDbFS, setNoiseLevelDbFS] = useState(0);
  const [calibratedLevelDbSPL, setCalibratedLevelDbSPL] = useState(0);
  const [updateInterval, setUpdateInterval] = useState(100); // интервал в мс

  const maxCalibratedLevelDbSPLRef = useRef(0);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneStreamRef = useRef(null);
  const intervalRef = useRef(null); // для хранения ID интервала

  const [isActiveFake, setisActiveFake] = useState(false);
  const intervalFakeRef = useRef(null); // для хранения ID интервала fake

  // Добавляем useRef для хранения сглаженного значения между рендерами
  const smoothedDbFSRef = useRef(0);

  // Новые состояния для информации о микрофоне
  const [currentMicrophone, setCurrentMicrophone] = useState(null);

  // Добавляем параметр для увеличения/уменьшения уровня
  const [levelBoost, setLevelBoost] = useState(0);
  const [isBoosting, setIsBoosting] = useState(false);

  // Ref для текущего значения boost (используется в analyzeAudio)
  const levelBoostRef = useRef(0);

  // Ref для анимации
  const boostAnimationRef = useRef({
    animationId: null,
  });

  // Функция для плавного добавления boost
  const addSmoothBoost = useCallback((changeLevel = 20) => {
    if (isBoosting) return;

    const startBoost = levelBoostRef.current;
    const targetBoost = startBoost + changeLevel;

    setIsBoosting(true);
    const startTime = performance.now();
    const duration = deltaDuration;

    const animate = (timestamp) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Кубическая easing функция
      const easeProgress =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      const newBoost = Math.round(
        startBoost + (targetBoost - startBoost) * easeProgress,
      );

      // Обновляем и ref и состояние
      levelBoostRef.current = newBoost;
      setLevelBoost(newBoost);

      if (progress < 1) {
        boostAnimationRef.current.animationId = requestAnimationFrame(animate);
      } else {
        // Завершение анимации
        levelBoostRef.current = targetBoost;
        setLevelBoost(targetBoost);
        setIsBoosting(false);
        boostAnimationRef.current.animationId = null;
      }
    };

    boostAnimationRef.current.animationId = requestAnimationFrame(animate);
  }, []);

  // Функция для получения нормализованного названия микрофона
  const getMicrophoneName = async (deviceId) => {
    try {
      // Получаем список всех устройств после получения разрешения
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphone = devices.find(
        (device) =>
          device.kind === 'audioinput' && device.deviceId === deviceId,
      );

      return microphone?.label || 'Микрофон по умолчанию';
    } catch (error) {
      console.error('Ошибка получения названия микрофона:', error);
      return 'Микрофон';
    }
  };

  const getCurrentMicrophoneInfo = async (stream) => {
    try {
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) return null;

      const settings = audioTrack.getSettings();
      const deviceId = settings.deviceId || 'default';

      // Получаем нормализованное название микрофона
      const microphoneName = await getMicrophoneName(deviceId);

      const microphoneInfo = {
        label: microphoneName,
        deviceId: deviceId,
        sampleRate:
          settings.sampleRate || audioContextRef.current?.sampleRate || 48000,
        channelCount: settings.channelCount || 1,
      };

      setCurrentMicrophone(microphoneInfo);
      return microphoneInfo;
    } catch (error) {
      console.error('Ошибка получения информации о микрофоне:', error);
      setCurrentMicrophone({
        label: 'Неизвестный микрофон',
        error: error.message,
      });
      return null;
    }
  };

  const analyzeAudio = () => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    // 1. Используем частотную область
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);

    // 2. Вычисляем среднюю мощность (правильный способ)
    let sumPower = 0;
    for (let i = 0; i < dataArray.length; i++) {
      // Конвертируем значение из байта (0-255) в линейную мощность
      const normalizedDb = dataArray[i] / 255;
      const power = Math.pow(10, (normalizedDb * 100 - 100) / 20);
      sumPower += power;
    }
    const averagePower = sumPower / dataArray.length;

    // 3. Конвертируем среднюю мощность обратно в dBFS
    let currentDbFS = -100;
    if (averagePower > 0) {
      currentDbFS = 20 * Math.log10(averagePower);
      currentDbFS = Math.max(currentDbFS, -100);
    }

    // 4. Применяем алгоритм скользящего среднего
    // const smoothingFactor = smoothingFactor || 0.0; // Чем ближе к 1, тем плавнее
    // smoothedDbFSRef.current - предыдущее сглаженное значение
    // currentDbFS - новое "сырое" значение
    smoothedDbFSRef.current =
      smoothedDbFSRef.current * smoothingFactor +
      currentDbFS * (1 - smoothingFactor);

    // 5. Устанавливаем сглаженное значение в состояние
    setNoiseLevelDbFS(smoothedDbFSRef.current);
    // 6. Применяем калибровочное смещение
    const estimatedDbSPL = Math.max(
      smoothedDbFSRef.current + calibrationOffset + levelBoostRef.current,
      1,
    );

    setCalibratedLevelDbSPL(estimatedDbSPL.toFixed(1));

    maxCalibratedLevelDbSPLRef.current = Math.max(
      estimatedDbSPL,
      maxCalibratedLevelDbSPLRef.current,
    );
  };

  // При отключенном микрофоне, колебания значений
  const disabledMicrophon = () => {
    let fakeValue = 40 + levelBoostRef.current;
    setCalibratedLevelDbSPL(fakeValue.toFixed(1));
  };

  const disabledMicrophon1 = () => {
    let fakeValue = 40 + Math.random() * 2;
    setCalibratedLevelDbSPL(fakeValue.toFixed(1));
  };

  const startFake = () => {
    setisActiveFake(true);
    intervalFakeRef.current = setInterval(disabledMicrophon, updateInterval);
    setTimeout(() => {
      clearInterval(intervalFakeRef.current);
      intervalFakeRef.current = null;
      intervalFakeRef.current = setInterval(disabledMicrophon1, updateInterval);
    }, deltaDuration);
  };

  const stopFake = () => {
    setisActiveFake(false);
    if (intervalFakeRef.current) {
      clearInterval(intervalFakeRef.current);
      intervalFakeRef.current = null;
    }
  };

  const startMeasurement = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Основные настройки для отключения обработки
          autoGainControl: { exact: false },
          echoCancellation: { exact: false },
          noiseSuppression: { exact: false },
          sampleRate: 48000,
          channelCount: 1,
        },
        video: false,
      });
      microphoneStreamRef.current = stream;

      // Получаем информацию о микрофоне
      await getCurrentMicrophoneInfo(stream);

      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;

      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);

      analyzeAudio();

      // Запускаем интервал с заданной периодичностью
      intervalRef.current = setInterval(analyzeAudio, updateInterval);

      setIsActiveMeasurement(true);
    } catch (err) {
      console.error('Ошибка доступа к микрофону:', err);
    }
  };

  const stopMeasurement = () => {
    // Останавливаем интервал
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }

    if (boostAnimationRef.current.animationId) {
      cancelAnimationFrame(boostAnimationRef.current.animationId);
      boostAnimationRef.current.animationId = null;
    }
    setIsBoosting(false);

    audioContextRef.current = null;
    analyserRef.current = null;
    microphoneStreamRef.current = null;

    setIsActiveMeasurement(false);

    // setNoiseLevelDbFS(0);
    // setCalibratedLevelDbSPL(0);
  };

  return {
    noiseLevelDbFS,
    calibratedLevelDbSPL,
    startMeasurement, // Функция запуска
    stopMeasurement, // Функция остановки
    currentMicrophone,
    addSmoothBoost,
    levelBoost,
    isActiveMeasurement,
    startFake,
    stopFake,
    isActiveFake,
    maxCalibratedLevelDbSPL: maxCalibratedLevelDbSPLRef.current.toFixed(1),
    setCalibratedLevelDbSPL,
  };
};
